import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LogOut, LayoutDashboard, ShoppingCart, CreditCard, Package, Shield, 
  ClipboardCheck, Users, Calendar, XCircle, Truck, Warehouse, BarChart3,
  TrendingUp, AlertTriangle, Clock, CheckCircle, Euro,
  RefreshCw, FileText, ChevronRight, User, Plus, Receipt, Building2
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { clearSessionToken } from '../lib/auth';
import { toast } from 'sonner';
import ReturnRequestsView from './admin/ReturnRequestsView';
import InvoicesView from './admin/InvoicesView';
import { PackName } from './ui/PackName';

// Extracted views
import {
  AnalyticsView, ManualOrdersView, InspectionsView, QuotesView,
  OrdersView, PaymentsView, RentalsView, CalendarView,
  DepositsView, StockView, ShippingView, CustomersView, SEPARequestsView, AbandonedReservationsView
} from './admin/views';

const LOGO_WHITE = process.env.REACT_APP_LOGO_URL;
const API = `${window.location.origin}/api`;

const navItems = [
  { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { id: 'orders', label: 'Commandes', icon: ShoppingCart },
  { id: 'manual_orders', label: 'Commandes manuelles', icon: Plus },
  { id: 'quotes', label: 'Devis', icon: FileText },
  { id: 'invoices', label: 'Facturation', icon: Receipt },
  { id: 'payments', label: 'Paiements', icon: CreditCard },
  { id: 'sepa', label: 'Demandes SEPA', icon: Building2 },
  { id: 'abandoned', label: 'Réservations abandonnées', icon: AlertTriangle },
  { id: 'rentals', label: 'Parc en location', icon: Package },
  { id: 'calendar', label: 'Calendrier', icon: Calendar },
  { id: 'deposits', label: 'Cautions', icon: Shield },
  { id: 'inspections', label: 'États des lieux', icon: ClipboardCheck },
  { id: 'cancellations', label: 'Résiliations', icon: XCircle },
  { id: 'shipping', label: 'Frais transport', icon: Truck },
  { id: 'stock', label: 'Stock', icon: Warehouse },
  { id: 'customers', label: 'Clients', icon: Users },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sepaPendingCount, setSepaPendingCount] = useState(0);
  const [abandonedCount, setAbandonedCount] = useState(0);
  const [resiliationCount, setResiliationCount] = useState(0);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [stock, setStock] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [inspectionStats, setInspectionStats] = useState(null);
  const [packOptions, setPackOptions] = useState([]);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    checkAuth();
  }, []);

  // Read ?view= from URL to allow deep-linking (used by SEPA admin emails)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get('view');
    if (v) setActiveTab(v);
  }, [location.search]);

  // Poll SEPA pending count for sidebar badge (every 60s)
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/admin/sepa-requests/pending-count`, { withCredentials: true });
        if (!cancelled) setSepaPendingCount(r.data?.count || 0);
      } catch { /* silent */ }
      try {
        const r2 = await axios.get(`${API}/admin/abandoned-reservations/count`, { withCredentials: true });
        if (!cancelled) setAbandonedCount(r2.data?.count || 0);
      } catch { /* silent */ }
      try {
        const r3 = await axios.get(`${API}/admin/return-requests/pending-count`, { withCredentials: true });
        if (!cancelled) setResiliationCount(r3.data?.count || 0);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!response.data.is_admin) { navigate('/dashboard'); return; }
      setUser(response.data);
      fetchAllData();
    } catch (error) { navigate('/login'); }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, reservationsRes, stockRes, analyticsRes, calendarRes, inspectionsRes, manualOrdersRes, packOptionsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/reservations`, { withCredentials: true }),
        axios.get(`${API}/admin/stock`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/calendar`, { withCredentials: true }),
        axios.get(`${API}/admin/inspections/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/manual-orders`, { withCredentials: true }),
        axios.get(`${API}/packs`, { withCredentials: true }),
        axios.get(`${API}/admin/quotes`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      
      const regularReservations = reservationsRes.data || [];
      const allReservations = [...regularReservations].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setReservations(allReservations);
      setStock(stockRes.data);
      setAnalytics(analyticsRes.data);
      setCalendarEvents(calendarRes.data.events || []);
      setInspectionStats(inspectionsRes.data);
      const pOptions = (packOptionsRes.data || []).map(p => ({
        id: p.id, name: p.name, surface: p.surface, price: p.price,
        deposit: p.caution, description: p.description, size: p.size
      }));
      setPackOptions(pOptions);
      setQuotes(quotesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearSessionToken();
      navigate('/login');
    } catch (error) { clearSessionToken(); navigate('/login'); }
  };

  const handleApproveBooking = async (reservationId) => {
    try {
      await axios.post(`${API}/admin/bookings/${reservationId}/approve`, {}, { withCredentials: true });
      toast.success('Commande approuvée avec succès');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation");
    }
  };

  const handleRejectBooking = async (reservationId, reason = '') => {
    const rejectReason = reason || prompt('Motif du refus (optionnel):') || "Commande refusée par l'administrateur";
    try {
      await axios.post(`${API}/admin/bookings/${reservationId}/reject`, { reason: rejectReason }, { withCredentials: true });
      toast.success('Commande refusée - Remboursement initié');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: { color: 'bg-blue-500/20 text-blue-400', text: 'Confirmé' },
      pending: { color: 'bg-yellow-500/20 text-yellow-400', text: 'En attente' },
      pending_validation: { color: 'bg-orange-500/20 text-orange-400', text: 'À valider' },
      cancelled: { color: 'bg-red-500/20 text-red-400', text: 'Annulé' },
      rejected: { color: 'bg-red-500/20 text-red-400', text: 'Refusé' },
      active: { color: 'bg-blue-500/20 text-blue-400', text: 'Actif' },
      shipped: { color: 'bg-green-500/20 text-green-400', text: 'Expédié' },
      return_pending: { color: 'bg-orange-500/20 text-orange-400', text: 'Retour en cours' },
      returned: { color: 'bg-purple-500/20 text-purple-400', text: 'Retourné' },
      terminated: { color: 'bg-slate-500/20 text-slate-400', text: 'Terminé' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>{badge.text}</span>;
  };

  // Dashboard Overview (inline — small enough to keep here)
  const DashboardOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-5 border border-green-500/20">
          <div className="flex items-center justify-between mb-3">
            <Euro className="w-8 h-8 text-green-400" />
            <span className={`text-xs px-2 py-1 rounded-full ${
              (analytics?.revenue_growth || 0) >= 0 ? 'text-green-400 bg-green-500/20' : 'text-red-400 bg-red-500/20'
            }`}>{(analytics?.revenue_growth || 0) >= 0 ? '+' : ''}{analytics?.revenue_growth || 0}%</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatPrice(stats?.total_revenue || 0)}</p>
          <p className="text-sm text-slate-400">Chiffre d'affaires</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-5 border border-blue-500/20">
          <div className="flex items-center justify-between mb-3">
            <Package className="w-8 h-8 text-blue-400" />
            <TrendingUp className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-white">{stats?.active_subscriptions || 0}</p>
          <p className="text-sm text-slate-400">Locations actives</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-xl p-5 border border-yellow-500/20">
          <div className="flex items-center justify-between mb-3">
            <Shield className="w-8 h-8 text-yellow-400" />
          </div>
          <p className="text-2xl font-bold text-white">{formatPrice(stats?.total_deposits || 0)}</p>
          <p className="text-sm text-slate-400">Cautions en cours</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-5 border border-purple-500/20">
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-white">{stats?.total_customers || 0}</p>
          <p className="text-sm text-slate-400">Clients</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Alertes
          </h3>
          <div className="space-y-3">
            {reservations.filter(r => r.status === 'pending_validation').length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20 cursor-pointer hover:bg-orange-500/20 transition-colors" onClick={() => setActiveTab('orders')}>
                <Clock className="w-5 h-5 text-orange-400" />
                <span className="text-sm text-slate-300">
                  <strong className="text-orange-400">{reservations.filter(r => r.status === 'pending_validation').length}</strong> commande(s) en attente de validation
                </span>
                <ChevronRight className="w-4 h-4 text-orange-400 ml-auto" />
              </div>
            )}
            {resiliationCount > 0 && (
              <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20 cursor-pointer hover:bg-red-500/20 transition-colors" onClick={() => setActiveTab('cancellations')} data-testid="dashboard-resiliation-alert">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-sm text-slate-300">
                  <strong className="text-red-400">{resiliationCount}</strong> demande(s) de résiliation en attente
                </span>
                <ChevronRight className="w-4 h-4 text-red-400 ml-auto" />
              </div>
            )}
            {(!reservations.filter(r => r.status === 'pending_validation').length && !stats?.pending_reservations && !resiliationCount) && (
              <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-sm text-slate-300">Aucune alerte en cours</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#DFFF00]" />
            Dernières commandes
          </h3>
          <div className="space-y-3">
            {reservations.slice(0, 4).map((res) => (
              <div key={res.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-white">{res.customer?.firstName} {res.customer?.lastName}</p>
                  <p className="text-xs text-slate-400"><PackName name={res.pack_name} /></p>
                </div>
                <div className="text-right">{getStatusBadge(res.status)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quotes Overview */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#DFFF00]" />
            Devis
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setActiveTab('quotes')} className="text-slate-400 hover:text-white text-xs" data-testid="dashboard-quotes-link">
            Voir tout <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-white">{quotes.length}</p>
            <p className="text-xs text-slate-400 mt-1">Total</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-slate-400">{quotes.filter(q => q.status === 'draft').length}</p>
            <p className="text-xs text-slate-400 mt-1">Brouillons</p>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-3 text-center border border-blue-500/20">
            <p className="text-2xl font-bold text-blue-400">{quotes.filter(q => q.status === 'sent').length}</p>
            <p className="text-xs text-blue-400 mt-1">Envoyés</p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3 text-center border border-green-500/20">
            <p className="text-2xl font-bold text-green-400">{quotes.filter(q => q.status === 'accepted').length}</p>
            <p className="text-xs text-green-400 mt-1">Acceptés</p>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3 text-center border border-red-500/20">
            <p className="text-2xl font-bold text-red-400">{quotes.filter(q => q.status === 'rejected').length}</p>
            <p className="text-xs text-red-400 mt-1">Refusés</p>
          </div>
        </div>
      </div>

      {/* Stock Overview */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Warehouse className="w-5 h-5 text-[#DFFF00]" />
          État du stock
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stock.map((item) => (
            <div key={item.pack_id} className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-sm font-medium text-white mb-2">{item.name}</p>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-2xl font-bold text-[#DFFF00]">{item.available}</p>
                  <p className="text-xs text-slate-400">disponible(s)</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">{item.rented} loué(s)</p>
                  <p className="text-xs text-slate-500">/ {item.total} total</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Route content to extracted view components
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardOverview />;
      case 'orders': return <OrdersView reservations={reservations} handleApproveBooking={handleApproveBooking} handleRejectBooking={handleRejectBooking} />;
      case 'manual_orders': return <ManualOrdersView packOptions={packOptions} />;
      case 'quotes': return <QuotesView packOptions={packOptions} />;
      case 'invoices': return <InvoicesView />;
      case 'payments': return <PaymentsView reservations={reservations} stats={stats} />;
      case 'sepa': return <SEPARequestsView />;
      case 'abandoned': return <AbandonedReservationsView />;
      case 'rentals': return <RentalsView reservations={reservations} />;
      case 'calendar': return <CalendarView calendarEvents={calendarEvents} stock={stock} />;
      case 'deposits': return <DepositsView reservations={reservations} stats={stats} fetchAllData={fetchAllData} />;
      case 'inspections': return <InspectionsView reservations={reservations} inspectionStats={inspectionStats} />;
      case 'cancellations': return <ReturnRequestsView />;
      case 'shipping': return <ShippingView />;
      case 'stock': return <StockView fetchAllData={fetchAllData} />;
      case 'customers': return <CustomersView />;
      case 'analytics': return <AnalyticsView analytics={analytics} />;
      default: return <DashboardOverview />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#DFFF00] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-black border-r border-slate-800 flex flex-col transition-all duration-300`}>
        <div className="p-4 border-b border-slate-800">
          <img src={LOGO_WHITE} alt="Phocea Rent" className={`${sidebarCollapsed ? 'w-8' : 'h-8'} w-auto`} />
        </div>
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-[#DFFF00]/10 text-[#DFFF00]'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span className="text-sm flex-1 text-left">{item.label}</span>}
                {item.id === 'sepa' && sepaPendingCount > 0 && (
                  <span
                    className={`${sidebarCollapsed ? 'absolute top-1 right-1 w-2 h-2 p-0' : 'px-2 py-0.5'} rounded-full bg-yellow-400 text-black text-[10px] font-bold leading-none flex items-center justify-center`}
                    data-testid="sepa-nav-badge"
                  >
                    {!sidebarCollapsed && sepaPendingCount}
                  </span>
                )}
                {item.id === 'abandoned' && abandonedCount > 0 && (
                  <span
                    className={`${sidebarCollapsed ? 'absolute top-1 right-1 w-2 h-2 p-0' : 'px-2 py-0.5'} rounded-full bg-orange-400 text-black text-[10px] font-bold leading-none flex items-center justify-center`}
                    data-testid="abandoned-nav-badge"
                  >
                    {!sidebarCollapsed && abandonedCount}
                  </span>
                )}
                {item.id === 'cancellations' && resiliationCount > 0 && (
                  <span
                    className={`${sidebarCollapsed ? 'absolute top-1 right-1 w-2 h-2 p-0' : 'px-2 py-0.5'} rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex items-center justify-center`}
                    data-testid="resiliation-nav-badge"
                  >
                    {!sidebarCollapsed && resiliationCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#DFFF00] flex items-center justify-center">
                <User className="w-4 h-4 text-black" />
              </div>
            )}
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-500">Admin</p>
              </div>
            )}
            <button onClick={handleLogout} className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-slate-900/50 border-b border-slate-800 px-6 py-4 sticky top-0 z-10 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-white">
              {navItems.find(n => n.id === activeTab)?.label || 'Dashboard'}
            </h1>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={fetchAllData} className="border-slate-700 text-slate-300">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </div>
        </header>
        <div className="p-6">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
